import Typography from '@mui/material/Typography';
import { useTags } from 'features/tags';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar18, SettingsSort18 } from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import { useTheme } from 'styled-components';

import { FilterContextMenu } from '../filter-context-menu';
import { MainContextMenu } from './main-context-menu';
import * as Styled from './page-top-bar.style';
import { SearchButton } from './search-button';
import { UserAvatar } from './user-avatar';

export type PageTopBarProps = {
  onCalendar?: () => void;
  calendarOpen?: boolean;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  onCalendar,
  calendarOpen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const activePath = useActivePath({ level: 2 });

  const {
    menuOpen: filterMenuOpen,
    openMenu: openFilterMenu,
    closeMenu: closeFilterMenu,
  } = useOpenMenu();
  const [showTagFilters, setShowTagFilters] = useState(false);
  const tags = useTags();

  const onFilter = tags.length ? () => setShowTagFilters(true) : undefined;

  const _onClose = () => {
    setShowTagFilters(false);
    closeFilterMenu();
  };

  return (
    <TopBarBase
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h6" component="h1">
            {t('pages.today.title')}
          </Typography>
        </Styled.Title>

        <Styled.Interactions>
          {theme.isMobile && <SearchButton />}

          {!theme.isMobile && (
            <IconButton
              icon={Calendar18}
              onClick={onCalendar}
              highlighted={calendarOpen}
              color={Colors.Default}
              preventDrag
            />
          )}

          <IconButton
            icon={SettingsSort18}
            ref={filterButtonRef}
            onClick={openFilterMenu}
            color={Colors.Default}
            preventDrag
          />

          {theme.isMobile && <UserAvatar />}
        </Styled.Interactions>
      </Styled.Container>

      {filterMenuOpen &&
        (showTagFilters ? (
          <FilterContextMenu
            onClose={_onClose}
            referenceElement={filterButtonRef}
          />
        ) : (
          <MainContextMenu
            onClose={_onClose}
            referenceElement={filterButtonRef}
            onFilter={onFilter}
          />
        ))}
    </TopBarBase>
  );
};
